/**
 * #439 Bento Grid 2026
 *
 * 可复用的 Bento Grid 模板组件，通过 props 传入数据即可展示任意主题。
 * 灵感来源：Apple Bento Box、Linear、Raycast 等现代应用
 *
 * 使用方式：
 *   <BentoGrid2026
 *     title="Q2 产品报告"
 *     subtitle="2026 年第二季度核心指标"
 *     cards={[
 *       { title: 'DAU', value: '128K', icon: '👥', span: { col: 2, row: 2 } },
 *       { title: '收入', value: '$2.4M', icon: '💰' },
 *       ...
 *     ]}
 *   />
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ============ 类型定义 ============

/** 单张卡片的输入数据 */
export interface BentoCardInput {
  title: string;
  subtitle?: string;
  icon?: string;
  value?: string;
  /** 自定义强调色，不传则自动从调色板分配 */
  color?: string;
  /** 卡片在网格中占据的列数和行数，默认 {col:1, row:1} */
  span?: { col?: number; row?: number };
  /** 入场延迟帧数，不传则按索引自动递增 */
  delay?: number;
}

/** 主题配色 */
export interface BentoTheme {
  background?: string;
  cardBg?: string;
  cardBorder?: string;
  textPrimary?: string;
  textSecondary?: string;
  /** 强调色调色板，按顺序分配给卡片 */
  accentPalette?: string[];
}

/** 组件 Props */
export interface BentoGrid2026Props {
  /** 页面主标题 */
  title?: string;
  /** 页面副标题 */
  subtitle?: string;
  /** 卡片数据列表 */
  cards?: BentoCardInput[];
  /** 主题配色（可选，有默认暗色主题） */
  theme?: BentoTheme;
  /** 网格列数，默认 4 */
  columns?: number;
}

// ============ 内部类型 ============

interface ResolvedCard {
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  value?: string;
  color: string;
  gridColumn: string;
  gridRow: string;
  delay: number;
}

// ============ 默认配色 ============

const DEFAULT_PALETTE = [
  '#a855f7', // purple
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // green
  '#f59e0b', // orange
  '#ec4899', // pink
];

const DEFAULT_THEME: Required<BentoTheme> = {
  background: '#0a0a0f',
  cardBg: 'rgba(255, 255, 255, 0.03)',
  cardBorder: 'rgba(255, 255, 255, 0.1)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.6)',
  accentPalette: DEFAULT_PALETTE,
};

// ============ 默认卡片数据（示例：产品仪表盘） ============

const DEFAULT_CARDS: BentoCardInput[] = [
  { title: 'Performance', subtitle: 'Lightning fast', icon: '⚡', value: '10x', span: { col: 2, row: 2 } },
  { title: 'Active Users', subtitle: 'This month', icon: '👥', value: '12.5K', span: { col: 2, row: 1 } },
  { title: 'Conversion', icon: '📈', value: '34.2%' },
  { title: 'Revenue', icon: '💰', value: '$89K' },
  { title: 'New Features', subtitle: 'Shipped this quarter', icon: '🚀', value: '24', span: { col: 2, row: 1 } },
  { title: 'Uptime', icon: '🔒', value: '99.9%', span: { col: 2, row: 1 } },
];

// ============ 布局计算 ============

function resolveCards(
  inputs: BentoCardInput[],
  palette: string[],
  columns: number,
): ResolvedCard[] {
  const grid: boolean[][] = [];

  function ensureRows(rowCount: number) {
    while (grid.length < rowCount) {
      grid.push(new Array(columns).fill(false));
    }
  }

  function findNextSlot(colSpan: number, rowSpan: number): { col: number; row: number } {
    for (let r = 0; ; r++) {
      ensureRows(r + rowSpan);
      for (let c = 0; c <= columns - colSpan; c++) {
        let fits = true;
        for (let dr = 0; dr < rowSpan && fits; dr++) {
          for (let dc = 0; dc < colSpan && fits; dc++) {
            ensureRows(r + dr + 1);
            if (grid[r + dr][c + dc]) fits = false;
          }
        }
        if (fits) return { col: c, row: r };
      }
    }
  }

  return inputs.map((input, i) => {
    const colSpan = input.span?.col ?? 1;
    const rowSpan = input.span?.row ?? 1;
    const slot = findNextSlot(colSpan, rowSpan);

    for (let dr = 0; dr < rowSpan; dr++) {
      for (let dc = 0; dc < colSpan; dc++) {
        grid[slot.row + dr][slot.col + dc] = true;
      }
    }

    return {
      id: `card-${i}`,
      title: input.title,
      subtitle: input.subtitle,
      icon: input.icon,
      value: input.value,
      color: input.color ?? palette[i % palette.length],
      gridColumn: `${slot.col + 1} / span ${colSpan}`,
      gridRow: `${slot.row + 1} / span ${rowSpan}`,
      delay: input.delay ?? i * 5,
    };
  });
}

// ============ Bento 卡片组件 ============

const BentoCardComponent: React.FC<{
  card: ResolvedCard;
  theme: Required<BentoTheme>;
  frame: number;
  fps: number;
}> = ({ card, theme, frame, fps }) => {
  const progress = spring({
    frame: frame - card.delay,
    fps,
    config: { damping: 20, stiffness: 100, mass: 0.8 },
  });

  const scale = interpolate(progress, [0, 1], [0.8, 1]);
  const opacity = interpolate(progress, [0, 1], [0, 1]);
  const translateY = interpolate(progress, [0, 1], [30, 0]);
  const rotateX = interpolate(progress, [0, 1], [15, 0]);

  return (
    <div
      style={{
        gridColumn: card.gridColumn,
        gridRow: card.gridRow,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '24px',
        background: `linear-gradient(135deg, ${card.color}15, ${card.color}05)`,
        borderRadius: '24px',
        border: `1px solid ${card.color}30`,
        backdropFilter: 'blur(20px)',
        transform: `perspective(1000px) rotateX(${rotateX}deg) scale(${scale}) translateY(${translateY}px)`,
        opacity,
        boxShadow: `0 4px 24px ${card.color}20, inset 0 1px 0 rgba(255,255,255,0.1)`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {card.icon && (
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: `linear-gradient(135deg, ${card.color}, ${card.color}80)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              boxShadow: `0 4px 12px ${card.color}40`,
            }}
          >
            {card.icon}
          </div>
        )}
        <div>
          <div style={{ fontSize: '28px', fontWeight: 600, color: theme.textPrimary, marginBottom: '6px' }}>
            {card.title}
          </div>
          {card.subtitle && (
            <div style={{ fontSize: '24px', color: theme.textSecondary }}>
              {card.subtitle}
            </div>
          )}
        </div>
      </div>

      {card.value && (
        <div style={{ fontSize: '44px', fontWeight: 700, color: card.color, marginTop: '20px' }}>
          {card.value}
        </div>
      )}

      {/* 光晕 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '120px',
          height: '120px',
          background: `radial-gradient(circle, ${card.color}30 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(30px)',
          pointerEvents: 'none',
          opacity: 0.6,
        }}
      />
    </div>
  );
};

// ============ 主组件 ============

export const BentoGrid2026: React.FC<BentoGrid2026Props> = ({
  title = 'Bento Grid 2026',
  subtitle = 'Modern Dashboard Design',
  cards: cardInputs = DEFAULT_CARDS,
  theme: themeOverride,
  columns = 4,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const theme: Required<BentoTheme> = { ...DEFAULT_THEME, ...themeOverride };
  const palette = theme.accentPalette;
  const cards = resolveCards(cardInputs, palette, columns);

  const globalProgress = spring({
    frame,
    fps,
    config: { damping: 30, stiffness: 100 },
  });
  const globalScale = interpolate(globalProgress, [0, 1], [0.9, 1]);

  return (
    <AbsoluteFill
      style={{
        background: theme.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        perspective: '1200px',
      }}
    >
      {/* 背景光晕 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 30% 30%, ${palette[0]}15 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, ${palette[1 % palette.length]}10 0%, transparent 50%)
          `,
        }}
      />

      {/* Bento Grid 容器 */}
      <div
        style={{
          width: '1100px',
          height: '700px',
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gridAutoRows: '1fr',
          gap: '20px',
          padding: '20px',
          transform: `scale(${globalScale})`,
          transformStyle: 'preserve-3d',
        }}
      >
        {cards.map((card) => (
          <BentoCardComponent
            key={card.id}
            card={card}
            theme={theme}
            frame={frame}
            fps={fps}
          />
        ))}
      </div>

      {/* 标题 */}
      {title && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '40px',
            fontWeight: 700,
            color: theme.textPrimary,
            opacity: spring({ frame: frame - 30, fps, config: { damping: 20 } }),
            letterSpacing: '-0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
      )}

      {/* 副标题 */}
      {subtitle && (
        <div
          style={{
            position: 'absolute',
            top: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '20px',
            color: theme.textSecondary,
            opacity: spring({ frame: frame - 35, fps, config: { damping: 20 } }),
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </div>
      )}
    </AbsoluteFill>
  );
};
